import { blogScanContent, type BlogScanLang } from "./content";

export const SITE_URL = "https://refoundapp.ca";
export const PUBLISHED_AT = "2026-06-04T08:00:00-04:00";
export const MODIFIED_AT = "2026-06-04T08:00:00-04:00";

export interface BlogScanSEOData {
  lang: BlogScanLang;
  htmlLang: string;
  path: string;
  url: string;
  urlFr: string;
  urlEn: string;
  ogImage: string;
  title: string;
  description: string;
  keywords: string;
  author: string;
  ogLocale: string;
  ogLocaleAlt: string;
  jsonLd: {
    article: object;
    breadcrumb: object;
    faq: object;
  };
}

export function getBlogScanSEO(lang: BlogScanLang): BlogScanSEOData {
  const c = blogScanContent[lang];
  const slug = "comment-numeriser-recus-application-mobile";
  const path = lang === "fr" ? `/blog/${slug}` : `/en/blog/${slug}`;
  const url = `${SITE_URL}${path}`;
  const urlFr = `${SITE_URL}/blog/${slug}`;
  const urlEn = `${SITE_URL}/en/blog/${slug}`;
  const ogImage = `${SITE_URL}/images/blog/scan/scan-1.jpg`;

  const title =
    lang === "fr"
      ? "Comment numériser des reçus avec une application mobile | ReFound"
      : "How to Scan Receipts with a Mobile App | ReFound";

  const description =
    lang === "fr"
      ? "Découvrez comment numériser vos reçus avec une application mobile et comment ReFound vous aide à conserver vos preuves d'achat, gérer vos retours et utiliser certains reçus directement en magasin."
      : "Learn how to scan your receipts with a mobile app and how ReFound helps you keep your proof of purchase, manage returns, and use receipts directly in store.";

  const keywords =
    lang === "fr"
      ? "numériser reçus application mobile, scanner reçu téléphone, application reçu Canada, OCR reçu, gestion reçus personnels, retour en magasin téléphone, code-barres retour, garantie reçu numérique, ReFound, preuve d'achat numérique"
      : "scan receipts mobile app, receipt scanner phone, receipt app Canada, OCR receipt, personal receipt management, in-store return phone, return barcode, digital warranty receipt, ReFound, digital proof of purchase";

  const article = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: c.hero.title,
    description,
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    datePublished: PUBLISHED_AT,
    dateModified: MODIFIED_AT,
    image: [ogImage],
    author: {
      "@type": "Organization",
      name: c.hero.author,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "ReFound",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon.png` },
    },
    articleSection: c.hero.breadcrumbCategory,
    keywords,
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: c.hero.breadcrumbHome,
        item: lang === "fr" ? `${SITE_URL}/` : `${SITE_URL}/en`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: c.hero.breadcrumbBlog,
        item: lang === "fr" ? `${SITE_URL}/blog` : `${SITE_URL}/en/blog`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: c.hero.breadcrumbCategory,
        item: url,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: c.hero.title,
      },
    ],
  };

  const faqItems = c.faq.items as ReadonlyArray<{ q: string; a: string }>;

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return {
    lang,
    htmlLang: lang === "fr" ? "fr-CA" : "en-CA",
    path,
    url,
    urlFr,
    urlEn,
    ogImage,
    title,
    description,
    keywords,
    author: c.hero.author,
    ogLocale: lang === "fr" ? "fr_CA" : "en_CA",
    ogLocaleAlt: lang === "fr" ? "en_CA" : "fr_CA",
    jsonLd: { article, breadcrumb, faq },
  };
}
